import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useInvalidatingMutation, useToastMutation } from '../utils/mutation';

export interface Permission {
  name: string;
  title: string;
  category: string;
}

// Shape of tagRestriction in the GET /role response — full tag objects
export interface TagRestrictionResponse {
  and: Array<{ id: string }>;
  or: Array<{ id: string }>;
  not: Array<{ id: string }>;
}

// Shape used for saves and TagBoolean state — just IDs
export interface TagRestriction {
  and: string[];
  or: string[];
  not: string[];
}

export interface Role {
  id: string;
  name: string;
  projectId: string;
  permissions: Permission[];
  canEdit: boolean;
  tagRestriction: TagRestrictionResponse;
}

export function useProjectRoles(projectId: string) {
  return useQuery<Role[]>({
    queryKey: ['roles', projectId],
    queryFn: () => api.get<Role[]>(`/project/${projectId}/role`),
    enabled: !!projectId,
  });
}

export function usePermissions(projectId: string) {
  return useQuery<Permission[]>({
    queryKey: ['permissions', projectId],
    queryFn: () => api.get<Permission[]>(`/project/${projectId}/permissions`),
    enabled: !!projectId,
  });
}

export interface PermissionPayload extends Permission {
  checked: boolean;
}

export interface SaveRolePayload {
  id?: string;
  name: string;
  permissions: PermissionPayload[];
  tagRestriction?: TagRestriction;
}

export function useSaveRole(projectId: string) {
  return useInvalidatingMutation(
    ['roles', projectId],
    (role: SaveRolePayload) =>
      role.id
        ? api.put<Role>(`/project/${projectId}/role/${role.id}`, role)
        : api.post<Role>(`/project/${projectId}/role`, role)
  );
}

export function useDeleteRole(projectId: string) {
  // The backend rejects deletion of an assigned role with a 400 + errors[] body
  // ("This role is assigned to users and cannot be deleted." etc); the global error
  // handler surfaces that server message, so only the success toast lives here.
  return useToastMutation(
    ['roles', projectId],
    (roleId: string) => api.delete(`/project/${projectId}/role/${roleId}`),
    {
      success: () => ({ title: 'Role removed successfully', timeout: 2500 }),
    }
  );
}
